import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import Dialog from "@/components/ui/Dialog";
import { CatalogPlan, PlanChangePreview, confirmPlanChange, previewPlanChange } from "@/services/stripe";
import { formatCount, formatDay, formatMoney } from "@/utils/billingFormat";

interface Props {
  target: string | null;
  currentPlan: string;
  catalog: CatalogPlan[];
  card: { brand: string; last4: string } | null;
  onClose: () => void;
  onDone: (result: { status: "done" | "scheduled"; plan: string; effectiveAt?: string }) => void;
  onOpenPortal: () => void;
}

const apiMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
const apiCode = (err: unknown) => (err as { response?: { data?: { code?: string } } })?.response?.data?.code;

export default function ChangePlanDialog({ target, currentPlan, catalog, card, onClose, onDone, onOpenPortal }: Props) {
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ message: string; code?: string } | null>(null);

  useEffect(() => {
    if (!target) return;
    setPreview(null);
    setError(null);
    setLoading(true);
    previewPlanChange(target)
      .then(setPreview)
      .catch(err => setError({ message: apiMessage(err, "Couldn't work out the price. Please try again."), code: apiCode(err) }))
      .finally(() => setLoading(false));
  }, [target]);

  const plan = catalog.find(p => p.name === target);
  const upgrade = preview?.direction === "upgrade";

  const confirm = async () => {
    if (!target || !preview) return;
    setSaving(true);
    setError(null);
    try {
      const result = await confirmPlanChange(target, preview.prorationDate);
      onDone({ status: result.status, plan: result.plan, effectiveAt: result.effectiveAt });
    } catch (err) {
      setError({ message: apiMessage(err, "Couldn't change your plan. Nothing was charged."), code: apiCode(err) });
    } finally {
      setSaving(false);
    }
  };

  const title = !target ? "" : !preview ? `Change to ${target}` : upgrade ? `Upgrade to ${target}` : `Switch to ${target}`;

  return (
    <Dialog isOpen={!!target} onClose={() => !saving && onClose()} title={title} width="md">
      <div className="space-y-5">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-6 justify-center">
            <Icon icon="eos-icons:loading" /> Working out the price...
          </div>
        )}

        {preview && plan && (
          <>
            <dl className="rounded-lg border border-gray-200 dark:border-white/10 divide-y divide-gray-100 dark:divide-white/5 text-sm">
              {upgrade ? (
                <>
                  <div className="flex items-start justify-between gap-4 p-3">
                    <dt className="text-gray-600 dark:text-gray-300">
                      Due today
                      <span className="block text-xs text-gray-400 dark:text-gray-500">A month of {plan.name} from today, less what&apos;s left of your current plan</span>
                    </dt>
                    <dd className="font-semibold text-gray-900 dark:text-white tabular-nums">{formatMoney(preview.dueTodayCents, preview.currency)}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4 p-3">
                    <dt className="text-gray-600 dark:text-gray-300">Then, from {formatDay(preview.renewsAt)}</dt>
                    <dd className="text-gray-900 dark:text-white tabular-nums">{formatMoney(plan.priceCents, plan.currency)} / month</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4 p-3">
                    <dt className="text-gray-600 dark:text-gray-300">Credits</dt>
                    <dd className="text-right text-gray-900 dark:text-white">
                      {formatCount(plan.includedCredits)} a month, starting now
                      <span className="block text-xs text-gray-400 dark:text-gray-500">Your billing date moves to today</span>
                    </dd>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4 p-3">
                    <dt className="text-gray-600 dark:text-gray-300">Due today</dt>
                    <dd className="font-semibold text-gray-900 dark:text-white">Nothing</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4 p-3">
                    <dt className="text-gray-600 dark:text-gray-300">Until {formatDay(preview.effectiveAt)}</dt>
                    <dd className="text-right text-gray-900 dark:text-white">You keep {currentPlan} and its credits</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4 p-3">
                    <dt className="text-gray-600 dark:text-gray-300">From {formatDay(preview.effectiveAt)}</dt>
                    <dd className="text-right text-gray-900 dark:text-white">
                      {target}, {formatMoney(plan.priceCents, plan.currency)} / month, {formatCount(plan.includedCredits)} credits
                    </dd>
                  </div>
                </>
              )}
            </dl>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              {upgrade
                ? `Your usage starts over with the new plan's full credits.${card ? ` We'll charge your ${card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} ending ${card.last4}.` : ""}`
                : "You can change your mind any time before then from this page."}
            </p>
          </>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30 p-3 text-sm text-red-700 dark:text-red-300">
            <p>{error.message}</p>
            {(error.code === "card_declined" || error.code === "payment_overdue") && (
              <button onClick={onOpenPortal} className="mt-1 font-medium underline cursor-pointer">
                Update your card
              </button>
            )}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            disabled={saving}
            className="h-10 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 rounded-md transition cursor-pointer"
          >
            {preview && !upgrade ? `Keep ${currentPlan}` : "Not now"}
          </button>
          <button
            onClick={confirm}
            disabled={!preview || saving}
            className="h-10 px-4 text-sm font-medium text-white bg-primary hover:bg-blue-700 rounded-md transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving && <Icon icon="eos-icons:loading" />}
            {!preview
              ? "Continue"
              : upgrade
                ? `Pay ${formatMoney(preview.dueTodayCents, preview.currency)} and upgrade`
                : `Switch on ${formatDay(preview.effectiveAt)}`}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
